import type { AnalyticsRange, SalesAnalytics } from './api'
import { translate } from './i18n'

/**
 * Unas analíticas de ventas verosímiles y FALSAS, para enseñar difuminadas
 * detrás de la oferta de plan.
 *
 * La misma regla que en `fakeAnalytics`, y no se negocia: quien no paga NUNCA
 * pide el endpoint real, así que ni siquiera las herramientas de desarrollo ven
 * cifras que no le corresponden. Lo que hay aquí son las cifras que se pintan
 * bajo el cristal — y por eso el cristal no se quita: nítidas se leerían como
 * ventas de verdad.
 *
 * Los números salen de una onda y no de `Math.random()`: con azar, cada
 * repintado movía la gráfica y el bloque parecía estar cargando algo. Una serie
 * estable se lee como lo que es, una muestra.
 */
export function fakeSalesAnalytics(range: AnalyticsRange = '30d', currency = 'USD'): SalesAnalytics {
  const points = 30
  const today = new Date()
  const series = Array.from({ length: points }, (_, i) => {
    const d = new Date(today)
    d.setUTCDate(d.getUTCDate() - (points - 1 - i))
    /* Menos ventas que visitas y con más dientes: una tienda pequeña factura a
       saltos, y una curva suave delataría el invento antes que las cifras. */
    const orders = Math.max(0, Math.round(3 + 2.4 * Math.sin(i / 2.3) + (i % 5 === 0 ? 2 : 0)))
    return { day: d.toISOString().slice(0, 10), orders, revenue: orders * (4_900 + (i % 4) * 1_500) }
  })

  const revenue = series.reduce((total, p) => total + p.revenue, 0)
  const orders = series.reduce((total, p) => total + p.orders, 0)
  const customers = Math.round(orders * 0.78)
  const money = (amount: number) => `$${(amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  return {
    range,
    currency,
    otherCurrencies: [],
    everSold: true,
    totals: {
      revenue,
      revenuePrev: Math.round(revenue * 0.84),
      gross: Math.round(revenue * 1.06),
      grossPrev: Math.round(revenue * 0.89),
      refunded: Math.round(revenue * 0.06),
      refundedPrev: Math.round(revenue * 0.05),
      refundedOrders: 2,
      orders,
      ordersPrev: Math.round(orders * 0.86),
      customers,
      customersPrev: Math.round(customers * 0.88),
      newCustomers: Math.round(customers * 0.62),
      returningCustomers: customers - Math.round(customers * 0.62),
      aov: orders > 0 ? Math.round(revenue / orders) : 0,
      aovPrev: orders > 0 ? Math.round((revenue * 0.84) / Math.max(1, Math.round(orders * 0.86))) : 0,
      subscriptionRevenue: Math.round(revenue * 0.31),
      subscriptionOrders: Math.round(orders * 0.24),
      oneTimeRevenue: revenue - Math.round(revenue * 0.31),
      oneTimeOrders: orders - Math.round(orders * 0.24),
      disputedOrders: 0,
      disputedAmount: 0,
    },
    granularity: 'day',
    series,
    /* Los nombres salen del catálogo, como los de `fakeOrders`: una muestra
       escrita en español dentro de un panel en inglés se lee como un fallo. */
    topOffers: [
      { offerId: null, title: translate('fakeOrders.item1'), image: null, revenue: Math.round(revenue * 0.38), orders: Math.round(orders * 0.3), formatted: money(Math.round(revenue * 0.38)) },
      { offerId: null, title: translate('fakeOrders.item2'), image: null, revenue: Math.round(revenue * 0.27), orders: Math.round(orders * 0.26), formatted: money(Math.round(revenue * 0.27)) },
      { offerId: null, title: translate('fakeOrders.item3'), image: null, revenue: Math.round(revenue * 0.19), orders: Math.round(orders * 0.22), formatted: money(Math.round(revenue * 0.19)) },
    ],
    topCustomers: [
      { email: 'ana@ejemplo.com', name: translate('fakeOrders.buyer1'), revenue: Math.round(revenue * 0.14), orders: 4, formatted: money(Math.round(revenue * 0.14)) },
      { email: 'luis@ejemplo.com', name: translate('fakeOrders.buyer2'), revenue: Math.round(revenue * 0.11), orders: 3, formatted: money(Math.round(revenue * 0.11)) },
      { email: 'marta@ejemplo.com', name: translate('fakeOrders.buyer3'), revenue: Math.round(revenue * 0.08), orders: 2, formatted: money(Math.round(revenue * 0.08)) },
    ],
    recentSales: [
      { id: 'sample-sale-1', offerId: null, offerTitle: translate('fakeOrders.item1'), buyerEmail: 'ana@ejemplo.com', buyerName: translate('fakeOrders.buyer1'), createdAt: series[series.length - 1]!.day, formatted: money(Math.round(revenue * 0.04)) },
      { id: 'sample-sale-2', offerId: null, offerTitle: translate('fakeOrders.item2'), buyerEmail: 'luis@ejemplo.com', buyerName: translate('fakeOrders.buyer2'), createdAt: series[series.length - 2]!.day, formatted: money(Math.round(revenue * 0.03)) },
      { id: 'sample-sale-3', offerId: null, offerTitle: translate('fakeOrders.item3'), buyerEmail: 'marta@ejemplo.com', buyerName: translate('fakeOrders.buyer3'), createdAt: series[series.length - 3]!.day, formatted: money(Math.round(revenue * 0.02)) },
    ],
    statuses: [
      { status: 'paid', orders: orders - 3, amount: Math.round(revenue * 0.94) },
      { status: 'partially_refunded', orders: 2, amount: Math.round(revenue * 0.04) },
      { status: 'refunded', orders: 1, amount: Math.round(revenue * 0.02) },
    ],
  }
}
